import { format } from 'date-fns'
import {
    endOfMonth,
    endOfWeek,
    endOfYear,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
} from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { MdDateRange } from 'react-icons/md'

/** Presets prontos de janela de datas para os filtros de listagem. */
export type DateRangePreset =
    | 'all'
    | 'today'
    | 'yesterday'
    | 'week'
    | 'month'
    | 'year'
    | 'custom'

const PRESET_LABELS: Record<DateRangePreset, string> = {
    all: 'Todo período',
    today: 'Hoje',
    yesterday: 'Ontem',
    week: 'Esta semana',
    month: 'Este mês',
    year: 'Este ano',
    custom: 'Personalizado',
}

const iso = (d: Date) => format(d, 'yyyy-MM-dd')

/** Converte um preset em `{ startDate, endDate }` (YYYY-MM-DD) no fuso local. */
function rangeFromPreset(preset: DateRangePreset): { startDate?: string; endDate?: string } {
    const now = new Date()
    switch (preset) {
        case 'today':
            return { startDate: iso(now), endDate: iso(now) }
        case 'yesterday': {
            const y = subDays(now, 1)
            return { startDate: iso(y), endDate: iso(y) }
        }
        case 'week':
            return {
                startDate: iso(startOfWeek(now, { weekStartsOn: 0 })),
                endDate: iso(endOfWeek(now, { weekStartsOn: 0 })),
            }
        case 'month':
            return { startDate: iso(startOfMonth(now)), endDate: iso(endOfMonth(now)) }
        case 'year':
            return { startDate: iso(startOfYear(now)), endDate: iso(endOfYear(now)) }
        default:
            return {}
    }
}

interface DateRangeFilterProps {
    /** Rótulo do campo (acima do select). */
    label?: string
    /** Recebe a janela efetiva sempre que muda. Datas vazias = sem filtro. */
    onChange: (range: { startDate?: string; endDate?: string }) => void
    className?: string
    /**
     * Preset inicial. Diferente de `all`, a janela correspondente é emitida na
     * montagem — senão a tela abriria mostrando "Hoje" no select e os dados de
     * todo o período.
     */
    defaultPreset?: DateRangePreset
    /**
     * `select` (padrão) mostra os presets num combo; `buttons` mostra todos como
     * botões prontos lado a lado, para telas onde trocar de período é a ação
     * principal (ex.: extrato).
     */
    variant?: 'select' | 'buttons'
}

/**
 * Filtro de período com presets (Hoje, Ontem, Esta semana, Este mês, Este ano) e opção
 * Personalizado que abre um range de datas. Auto-contido: gerencia o preset e as datas
 * custom internamente e apenas emite a janela resultante via `onChange`.
 */
export default function DateRangeFilter({
    label = 'Período',
    onChange,
    className = '',
    defaultPreset = 'all',
    variant = 'select',
}: DateRangeFilterProps) {
    const [preset, setPreset] = useState<DateRangePreset>(defaultPreset)
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')

    // Emite a janela do preset inicial uma única vez. `all` não emite: é o
    // comportamento histórico (sem filtro) e emitir `{}` dispararia uma busca
    // extra em todas as telas que já usam o componente.
    const emittedInitial = useRef(false)
    useEffect(() => {
        if (emittedInitial.current) return
        emittedInitial.current = true
        if (defaultPreset !== 'all' && defaultPreset !== 'custom') {
            onChange(rangeFromPreset(defaultPreset))
        }
        // Só na montagem: mudar `defaultPreset` depois não deve resetar o que o
        // operador escolheu no select.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handlePresetChange = (next: DateRangePreset) => {
        setPreset(next)
        if (next === 'custom') {
            // Só aplica quando ambas as datas estiverem preenchidas.
            if (customStart && customEnd) {
                onChange({ startDate: customStart, endDate: customEnd })
            } else {
                onChange({})
            }
            return
        }
        onChange(rangeFromPreset(next))
    }

    const handleCustomStart = (v: string) => {
        setCustomStart(v)
        if (v && customEnd) onChange({ startDate: v, endDate: customEnd })
        else onChange({})
    }

    const handleCustomEnd = (v: string) => {
        setCustomEnd(v)
        if (customStart && v) onChange({ startDate: customStart, endDate: v })
        else onChange({})
    }

    return (
        <div className={`flex flex-wrap items-end gap-3 ${className}`}>
            <div className="form-control">
                <label className="label py-0">
                    <span className="label-text text-xs font-medium">{label}</span>
                </label>
                {variant === 'buttons' ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                        {(Object.keys(PRESET_LABELS) as DateRangePreset[]).map(key => (
                            <button
                                key={key}
                                type="button"
                                className={`btn btn-xs sm:btn-sm ${preset === key ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                                onClick={() => handlePresetChange(key)}
                                aria-pressed={preset === key}
                            >
                                {PRESET_LABELS[key]}
                            </button>
                        ))}
                    </div>
                ) : (
                    <select
                        value={preset}
                        onChange={e => handlePresetChange(e.target.value as DateRangePreset)}
                        className="select select-bordered select-sm w-40"
                    >
                        {(Object.keys(PRESET_LABELS) as DateRangePreset[]).map(key => (
                            <option key={key} value={key}>
                                {PRESET_LABELS[key]}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {preset === 'custom' && (
                <>
                    <div className="form-control">
                        <label className="label py-0">
                            <span className="label-text text-xs font-medium">De</span>
                        </label>
                        <div className="relative">
                            <MdDateRange className="absolute left-2 top-1/2 -translate-y-1/2 z-10 text-base-content/40 pointer-events-none" />
                            <input
                                type="date"
                                value={customStart}
                                max={customEnd || undefined}
                                onChange={e => handleCustomStart(e.target.value)}
                                className="input input-bordered input-sm w-40 pl-8"
                            />
                        </div>
                    </div>
                    <div className="form-control">
                        <label className="label py-0">
                            <span className="label-text text-xs font-medium">Até</span>
                        </label>
                        <div className="relative">
                            <MdDateRange className="absolute left-2 top-1/2 -translate-y-1/2 z-10 text-base-content/40 pointer-events-none" />
                            <input
                                type="date"
                                value={customEnd}
                                min={customStart || undefined}
                                onChange={e => handleCustomEnd(e.target.value)}
                                className="input input-bordered input-sm w-40 pl-8"
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
